import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { logos } from '@/assets/images'
import { useTheme } from '@/context/ThemeContext'
import { WHATSAPP_URL } from '@/constants/whatsapp'

const homeAnchors = [
  { name: 'Ecossistema', href: '#ecossistema' },
  { name: 'Aplicativos', href: '#apps' },
  { name: 'Painel', href: '#dashboard' },
  { name: 'Planos', href: '#planos' },
]

const pageNav = [
  { name: 'Apps', href: '/apps' },
  { name: 'Soluções', href: '/solucoes' },
  { name: 'Sobre', href: '/sobre' },
  { name: 'Contato', href: '/contato' },
]

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const isHome = location.pathname === '/'
  const logoSrc = theme === 'dark' ? logos.logoWhite : logos.logo

  const navItems = isHome ? homeAnchors : pageNav

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-[18px]"
      style={{
        background: 'var(--sp-nav)',
        borderBottom: '1px solid var(--sp-border-2)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/"
            className="flex shrink-0 items-center"
            aria-label="SwimmingPool — página inicial"
          >
            <img
              src={logoSrc}
              alt="SwimmingPool"
              width={180}
              height={36}
              decoding="async"
              fetchPriority="high"
              className="h-9 w-auto"
            />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map((item) =>
              item.href.startsWith('#') ? (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-[14.5px] font-medium transition-colors sp-muted hover:text-[var(--sp-text)]"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-[14.5px] font-medium transition-colors ${
                    location.pathname === item.href
                      ? 'text-[var(--sp-blue)]'
                      : 'sp-muted hover:text-[var(--sp-text)]'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </nav>

          <div className="hidden items-center gap-3.5 md:flex">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Alternar tema"
              className="flex h-10 w-10 items-center justify-center rounded-[11px] text-base transition-colors"
              style={{
                border: '1px solid var(--sp-border)',
                background: 'var(--sp-surface)',
                color: 'var(--sp-text)',
              }}
            >
              {theme === 'light' ? '☾' : '☀'}
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="sp-btn-cta px-5 py-2.5 text-[14.5px]">
              Agendar demonstração
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="rounded-[11px] p-2 md:hidden"
            style={{ color: 'var(--sp-text)' }}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden"
              style={{ borderTop: '1px solid var(--sp-border-2)' }}
            >
              <div className="space-y-1 py-3">
                {navItems.map((item) =>
                  item.href.startsWith('#') ? (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block rounded-lg px-3 py-2 text-base font-medium sp-muted"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block rounded-lg px-3 py-2 text-base font-medium sp-muted"
                    >
                      {item.name}
                    </Link>
                  )
                )}
                <div className="flex items-center gap-3 px-3 pt-3">
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="flex h-10 w-10 items-center justify-center rounded-[11px]"
                    style={{
                      border: '1px solid var(--sp-border)',
                      background: 'var(--sp-surface)',
                    }}
                  >
                    {theme === 'light' ? '☾' : '☀'}
                  </button>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="sp-btn-cta flex-1 py-3 text-sm"
                  >
                    Agendar demonstração
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

export default Header
